import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const STATUS_COLORS = {
  pending:        { bg:'#FEF6E0', color:'#E09A10', label:'Pending — Awaiting BM Recommendation' },
  bm_recommended: { bg:'#EBF5FF', color:'#1270B8', label:'BM Recommended — Awaiting Approver' },
  bm_declined:    { bg:'#FDEAEA', color:'#D63A3A', label:'Declined by Branch Manager' },
  under_review:   { bg:'#F3E8FF', color:'#7C3AED', label:'Under Review' },
  approved:       { bg:'#E6F7F1', color:'#0FA86A', label:'Approved' },
  declined:       { bg:'#FDEAEA', color:'#D63A3A', label:'Declined' },
};

function fmt(n) {
  if (!n && n !== 0) return '—';
  return new Intl.NumberFormat('en-NG', { style:'currency', currency:'NGN' }).format(n);
}

function getFileUrl(storedName) {
  if (!storedName) return '#';
  if (storedName.startsWith('http://') || storedName.startsWith('https://')) return storedName;
  return `/uploads/${storedName}`;
}

export default function SARequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approverNotes, setApproverNotes] = useState('');
  const [amountApproved, setAmountApproved] = useState('');
  const [bmRecommendedAmount, setBmRecommendedAmount] = useState('');
  const [bmNotes, setBmNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const isApprover = user?.role === 'sa_approver' || user?.role === 'ict_manager' || user?.role === 'super_admin';
  const isBranchManager = user?.role === 'branch_manager';
  const isInitiator = user?.role === 'sa_initiator';

  const load = async () => {
    try {
      const res = await api.get(`/api/sa/${id}`);
      setRequest(res.data);
      setAmountApproved(res.data.amount_approved || res.data.bm_recommended_amount || res.data.amount_requested || '');
      setBmRecommendedAmount(res.data.bm_recommended_amount || res.data.amount_requested || '');
    } catch { toast.error('Request not found'); navigate('/sa'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const handleBMRecommend = async () => {
    if (!bmRecommendedAmount) return toast.error('Please enter the recommended amount');
    setSaving(true);
    try {
      await api.put(`/api/sa/${id}`, { action: 'bm_recommend', bm_recommended_amount: bmRecommendedAmount, bm_notes: bmNotes });
      toast.success('✅ Recommendation submitted! Approver has been notified.');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleBMDecline = async () => {
    if (!window.confirm('Decline this request? It will not proceed to the approver.')) return;
    setSaving(true);
    try {
      await api.put(`/api/sa/${id}`, { action: 'bm_decline', bm_notes: bmNotes });
      toast.success('Request declined. Initiator has been notified.');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleAction = async (action) => {
    if (action === 'approve' && !amountApproved) return toast.error('Please enter the approved amount');
    setSaving(true);
    try {
      await api.put(`/api/sa/${id}`, { action, amount_approved: amountApproved, approver_notes: approverNotes });
      toast.success(action === 'approve' ? '✅ Request approved!' : action === 'decline' ? '❌ Request declined' : '🔍 Marked as under review');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleResubmit = () => {
    navigate('/sa/new', { state: {
      resubmitted_from: request.id,
      account_number: request.account_number,
      account_name: request.account_name,
    }});
  };

  const downloadPDF = () => {
    const approvedDate = request.decided_at ? format(new Date(request.decided_at), 'dd MMMM yyyy, HH:mm') : '—';
    const submittedDate = format(new Date(request.created_at), 'dd MMMM yyyy, HH:mm');
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>SA Receipt — ${request.request_number}</title>
    <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:13px;color:#1a1a2e;padding:40px}
    .header{text-align:center;border-bottom:3px solid #0E5F94;padding-bottom:20px;margin-bottom:24px}
    .bank-name{font-size:22px;font-weight:800;color:#0E5F94;letter-spacing:1px}.doc-title{font-size:14px;color:#666;margin-top:4px}
    .stamp{display:inline-block;border:3px solid #0FA86A;color:#0FA86A;font-size:18px;font-weight:800;padding:8px 24px;border-radius:8px;transform:rotate(-5deg);margin:12px 0;letter-spacing:2px}
    .stamp.declined{border-color:#D63A3A;color:#D63A3A}.ref{font-family:monospace;font-size:13px;font-weight:700;color:#0E5F94;background:#eff6ff;padding:6px 14px;border-radius:4px;display:inline-block;margin-bottom:20px}
    .section-title{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#888;border-bottom:1px solid #e5e7eb;padding-bottom:6px;margin:20px 0 12px}
    .grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}.field{background:#f9fafb;padding:10px 12px;border-radius:6px;border:1px solid #e5e7eb}
    .field-label{font-size:10px;color:#888;margin-bottom:4px;text-transform:uppercase}.field-value{font-size:13px;font-weight:600;color:#1a1a2e}
    .amount-box{text-align:center;padding:16px;background:#f0fdf4;border:2px solid #0FA86A;border-radius:8px;margin:20px 0}
    .amount-label{font-size:11px;color:#888;margin-bottom:6px;text-transform:uppercase}.amount-value{font-size:28px;font-weight:800;color:#0FA86A}
    .footer{margin-top:40px;padding-top:16px;border-top:1px solid #e5e7eb;text-align:center;font-size:10px;color:#aaa}
    @media print{body{padding:20px}}</style></head><body>
    <div class="header"><div class="bank-name">OMIYE MICROFINANCE BANK</div><div class="doc-title">Salary Advance / Overdraft — Official Receipt</div>
    <div style="margin-top:12px"><div class="stamp ${request.status==='declined'?'declined':''}">${request.status==='approved'?'✓ APPROVED':request.status==='declined'?'✗ DECLINED':request.status.toUpperCase()}</div></div></div>
    <div style="text-align:center"><div class="ref">${request.request_number}</div></div>
    <div class="section-title">Account Information</div>
    <div class="grid">
      <div class="field"><div class="field-label">Account Name</div><div class="field-value">${request.account_name}</div></div>
      <div class="field"><div class="field-label">Account Number</div><div class="field-value">${request.account_number}</div></div>
      <div class="field"><div class="field-label">Branch</div><div class="field-value">${request.branch}</div></div>
      <div class="field"><div class="field-label">Initiator</div><div class="field-value">${request.initiator_name}</div></div>
    </div>
    <div class="section-title">Salary Information</div>
    <div class="grid">
      <div class="field"><div class="field-label">Previous Salary Month</div><div class="field-value">${request.previous_salary_month}</div></div>
      <div class="field"><div class="field-label">Previous Salary Amount</div><div class="field-value">${fmt(request.previous_salary_amount)}</div></div>
      <div class="field"><div class="field-label">Amount Requested</div><div class="field-value">${fmt(request.amount_requested)}</div></div>
      ${request.current_commitment ? `<div class="field"><div class="field-label">Current Commitment</div><div class="field-value">${request.current_commitment}</div></div>` : ''}
    </div>
    ${request.bm_recommended_amount ? `
    <div class="section-title">Branch Manager Recommendation</div>
    <div class="grid">
      <div class="field"><div class="field-label">Recommended By</div><div class="field-value">${request.bm_name||'Branch Manager'}</div></div>
      <div class="field"><div class="field-label">Recommended Amount</div><div class="field-value">${fmt(request.bm_recommended_amount)}</div></div>
      ${request.bm_notes?`<div class="field" style="grid-column:1/-1"><div class="field-label">BM Notes</div><div class="field-value" style="font-weight:400">${request.bm_notes}</div></div>`:''}
    </div>` : ''}
    ${request.status==='approved'?`<div class="amount-box"><div class="amount-label">Approved Amount</div><div class="amount-value">${fmt(request.amount_approved)}</div></div>`:''}
    ${request.approver_notes?`<div class="section-title">Approver Notes</div><div class="field"><div class="field-value" style="font-weight:400">${request.approver_notes}</div></div>`:''}
    <div class="section-title">Timeline</div>
    <div class="grid">
      <div class="field"><div class="field-label">Date Submitted</div><div class="field-value">${submittedDate}</div></div>
      <div class="field"><div class="field-label">Date Decided</div><div class="field-value">${approvedDate}</div></div>
    </div>
    <div class="footer">This document is an official record from OMIYE MFB Internal HelpDesk.<br/>Generated on ${format(new Date(),'dd MMMM yyyy, HH:mm')} · ${request.request_number}</div>
    </body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 400);
  };

  if (loading) return <div className="loading">⏳ Loading…</div>;
  if (!request) return null;

  const s = STATUS_COLORS[request.status] || STATUS_COLORS.pending;

  // Workflow stages for the progress tracker
  const stages = [
    { key: 'pending',        label: 'Submitted',         done: true },
    { key: 'bm_recommended', label: 'BM Recommended',    done: ['bm_recommended','approved','declined','under_review'].includes(request.status) },
    { key: 'approved',       label: 'Final Decision',     done: ['approved','declined'].includes(request.status) },
  ];
  const bmDeclined = request.status === 'bm_declined';

  return (
    <div>
      <button className="btn btn-ghost btn-sm" style={{ marginBottom:14 }} onClick={() => navigate('/sa')}>← Back to Requests</button>

      <div className="table-wrap">
        {/* Header */}
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', background:'var(--surface)', borderRadius:'14px 14px 0 0', display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10 }}>
          <div>
            <div style={{ fontFamily:'JetBrains Mono,monospace', fontSize:11, fontWeight:600, color:'var(--sky)', marginBottom:4 }}>{request.request_number}</div>
            <div style={{ fontFamily:'Sora,sans-serif', fontSize:16, fontWeight:700, color:'var(--text)', marginBottom:8 }}>Salary Advance / Overdraft Request</div>
            <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
              <span style={{ fontSize:11, color:'var(--text3)' }}>📅 {format(new Date(request.created_at),'dd MMM yyyy, HH:mm')}</span>
              <span style={{ fontSize:11, color:'var(--text3)' }}>🏢 {request.branch}</span>
              <span style={{ fontSize:11, color:'var(--text3)' }}>👤 {request.initiator_name}</span>
            </div>
          </div>
          <span style={{ background:s.bg, color:s.color, padding:'4px 14px', borderRadius:12, fontSize:12, fontWeight:600 }}>{s.label}</span>
        </div>

        {/* Workflow Progress */}
        <div style={{ padding:'12px 20px', borderBottom:'1px solid var(--border)', background:'var(--white)', display:'flex', alignItems:'center', gap:0 }}>
          {stages.map((stage, i) => (
            <React.Fragment key={stage.key}>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                <div style={{
                  width:28, height:28, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
                  background: stage.done ? '#0FA86A' : 'var(--border)',
                  color: stage.done ? '#fff' : 'var(--muted)',
                  fontSize:13, fontWeight:700, flexShrink:0,
                }}>
                  {stage.done ? '✓' : i + 1}
                </div>
                <div style={{ fontSize:10, color: stage.done ? '#0FA86A' : 'var(--muted)', fontWeight:600, whiteSpace:'nowrap' }}>{stage.label}</div>
              </div>
              {i < stages.length - 1 && (
                <div style={{ flex:1, height:2, background: stages[i+1].done ? '#0FA86A' : 'var(--border)', margin:'0 6px', marginBottom:16 }} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 290px' }}>
          <div style={{ padding:'18px 20px', borderRight:'1px solid var(--border)' }}>

            {/* Request Details */}
            <div style={{ background:'var(--sky-pale)', border:'1px solid var(--border)', borderLeft:'3px solid var(--sky)', borderRadius:'var(--radius)', padding:'14px 16px', marginBottom:16 }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--sky2)', textTransform:'uppercase', letterSpacing:1, marginBottom:12 }}>Request Details</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                {[
                  { label:'Account Number', val: request.account_number },
                  { label:'Account Name', val: request.account_name },
                  { label:'Previous Salary Month', val: request.previous_salary_month },
                  { label:'Previous Salary Amount', val: fmt(request.previous_salary_amount) },
                  { label:'Amount Requested', val: fmt(request.amount_requested), highlight:true },
                  { label:'Current Commitment', val: request.current_commitment || '—' },
                  ...(request.bm_recommended_amount ? [{ label:'BM Recommended Amount', val: fmt(request.bm_recommended_amount), highlight:true, blue:true }] : []),
                  ...(request.amount_approved ? [{ label:'Amount Approved', val: fmt(request.amount_approved), highlight:true, green:true }] : []),
                ].map(f => (
                  <div key={f.label} style={{ background:'var(--white)', padding:'10px 12px', borderRadius:8, border:'1px solid var(--border)' }}>
                    <div style={{ fontSize:10, color:'var(--text3)', marginBottom:4 }}>{f.label}</div>
                    <div style={{ fontSize:f.highlight?15:13, fontWeight:f.highlight?700:500, color: f.green?'#0FA86A':f.blue?'#1270B8':f.highlight?'var(--sky2)':'var(--text)' }}>{f.val}</div>
                  </div>
                ))}
              </div>

              {request.notes && (
                <div style={{ marginTop:12, padding:'10px 12px', background:'var(--white)', borderRadius:8, border:'1px solid var(--border)' }}>
                  <div style={{ fontSize:10, color:'var(--text3)', marginBottom:4 }}>Notes from Initiator</div>
                  <div style={{ fontSize:12, color:'var(--text)', lineHeight:1.6 }}>{request.notes}</div>
                </div>
              )}
            </div>

            {/* BM Recommendation section */}
            {request.bm_recommended_amount && (
              <div style={{ background:'#EBF5FF', border:'1px solid #1270B8', borderLeft:'3px solid #1270B8', borderRadius:'var(--radius)', padding:'14px 16px', marginBottom:16 }}>
                <div style={{ fontSize:10, fontWeight:700, color:'#1270B8', textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>🏦 Branch Manager Recommendation</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div style={{ background:'var(--white)', padding:'10px 12px', borderRadius:8, border:'1px solid var(--border)' }}>
                    <div style={{ fontSize:10, color:'var(--text3)', marginBottom:4 }}>Recommended By</div>
                    <div style={{ fontSize:13, fontWeight:600 }}>{request.bm_name || '—'}</div>
                  </div>
                  <div style={{ background:'var(--white)', padding:'10px 12px', borderRadius:8, border:'1px solid var(--border)' }}>
                    <div style={{ fontSize:10, color:'var(--text3)', marginBottom:4 }}>Recommended Amount</div>
                    <div style={{ fontSize:15, fontWeight:700, color:'#1270B8' }}>{fmt(request.bm_recommended_amount)}</div>
                  </div>
                </div>
                {request.bm_notes && (
                  <div style={{ marginTop:10, padding:'10px 12px', background:'var(--white)', borderRadius:8, border:'1px solid var(--border)' }}>
                    <div style={{ fontSize:10, color:'var(--text3)', marginBottom:4 }}>BM Notes</div>
                    <div style={{ fontSize:12, color:'var(--text)', lineHeight:1.6 }}>{request.bm_notes}</div>
                  </div>
                )}
                {request.bm_recommended_at && (
                  <div style={{ fontSize:10, color:'var(--text3)', marginTop:8 }}>
                    Recommended on {format(new Date(request.bm_recommended_at), 'dd MMM yyyy, HH:mm')}
                  </div>
                )}
              </div>
            )}

            {request.approver_notes && (
              <div style={{ marginBottom:16, padding:'10px 12px', background: request.status==='approved'?'#E6F7F1':'#FDEAEA', borderRadius:8, border:`1px solid ${request.status==='approved'?'#0FA86A':'#D63A3A'}` }}>
                <div style={{ fontSize:10, color:'var(--text3)', marginBottom:4 }}>Notes from Approver</div>
                <div style={{ fontSize:12, color:'var(--text)', lineHeight:1.6 }}>{request.approver_notes}</div>
              </div>
            )}

            {/* Attachments */}
            {request.attachments?.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:8 }}>📎 Attachments</div>
                {request.attachments.map(a => (
                  <div key={a.id} style={{ display:'flex', alignItems:'center', gap:10, padding:10, background:'var(--surface)', borderRadius:'var(--radius)', marginBottom:6, border:'1px solid var(--border)' }}>
                    <span>📄</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12, fontWeight:500 }}>{a.original_name}</div>
                      <div style={{ fontSize:10, color:'var(--text3)' }}>{(a.file_size/1024).toFixed(1)} KB</div>
                    </div>
                    <a href={getFileUrl(a.stored_name)} target="_blank" rel="noreferrer" download={a.original_name} className="btn btn-primary btn-sm">⬇ Download</a>
                  </div>
                ))}
              </div>
            )}

            {/* Audit Trail */}
            {request.audit?.length > 0 && (
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:8 }}>📋 Audit Trail</div>
                {request.audit.map(a => (
                  <div key={a.id} className="audit-item">
                    <span>•</span>
                    <div>
                      <strong style={{ color:'var(--text2)' }}>{a.action}</strong> — {a.user_name || 'System'}
                      <span style={{ marginLeft:8, fontFamily:'JetBrains Mono,monospace', fontSize:10, color:'var(--text3)' }}>
                        {format(new Date(a.created_at),'dd MMM yyyy, HH:mm')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right sidebar */}
          <div style={{ padding:16 }}>

            {/* Branch Manager recommendation panel */}
            {isBranchManager && request.status === 'pending' && request.branch === user?.branch && (
              <div style={{ background:'var(--surface)', border:'1px solid #1270B8', borderRadius:'var(--radius)', padding:14, marginBottom:14 }}>
                <div style={{ fontFamily:'Sora,sans-serif', fontSize:12, fontWeight:700, color:'#1270B8', marginBottom:12 }}>
                  🏦 Branch Manager Recommendation
                </div>
                <div style={{ fontSize:11, color:'var(--text3)', marginBottom:12, lineHeight:1.6 }}>
                  Review this request and enter the amount you recommend for approval.
                </div>
                <div className="form-group">
                  <label className="form-label">Recommended Amount (₦)</label>
                  <input className="form-input" type="number" style={{ fontSize:12 }}
                    value={bmRecommendedAmount} onChange={e => setBmRecommendedAmount(e.target.value)}
                    placeholder="Enter amount you recommend" />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes <span style={{ color:'var(--text3)', fontWeight:400 }}>(optional)</span></label>
                  <textarea className="form-textarea" style={{ minHeight:70, fontSize:12 }}
                    placeholder="Any notes for the approver…"
                    value={bmNotes} onChange={e => setBmNotes(e.target.value)} />
                </div>
                <button className="btn btn-primary btn-sm" style={{ width:'100%', background:'#1270B8', border:'none' }}
                  onClick={handleBMRecommend} disabled={saving}>
                  {saving ? 'Submitting…' : '✅ Submit Recommendation'}
                </button>
                <button className="btn btn-primary btn-sm" style={{ width:'100%', background:'#D63A3A', border:'none', marginTop:8 }}
                  onClick={handleBMDecline} disabled={saving}>
                  ❌ Decline Request
                </button>
              </div>
            )}

            {/* BM already recommended — show status */}
            {isBranchManager && request.status !== 'pending' && (
              <div style={{ background:'#EBF5FF', border:'1px solid #1270B8', borderRadius:'var(--radius)', padding:14, marginBottom:14 }}>
                <div style={{ fontSize:12, fontWeight:600, color:'#1270B8', marginBottom:4 }}>✅ Recommendation Submitted</div>
                <div style={{ fontSize:11, color:'var(--text3)' }}>
                  {request.status === 'bm_recommended' ? 'Awaiting final decision from approver.' : `Request has been ${request.status}.`}
                </div>
              </div>
            )}

            {/* Approver actions — only after BM recommends (or if no BM) */}
            {isApprover && ['bm_recommended','under_review','pending'].includes(request.status) && (
              <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:14, marginBottom:14 }}>
                <div style={{ fontFamily:'Sora,sans-serif', fontSize:12, fontWeight:600, color:'var(--text)', marginBottom:12, display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ width:3, height:14, background:'linear-gradient(var(--sky),var(--orange))', borderRadius:2, display:'inline-block' }} />
                  Final Decision
                </div>
                {request.status === 'pending' && (
                  <div style={{ fontSize:11, color:'var(--warn)', background:'#FEF6E0', padding:'8px 10px', borderRadius:6, marginBottom:12, border:'1px solid #E09A10' }}>
                    ⚠️ Branch Manager has not yet submitted a recommendation.
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Amount to Approve (₦)</label>
                  <input className="form-input" type="number" style={{ fontSize:12 }}
                    value={amountApproved} onChange={e => setAmountApproved(e.target.value)}
                    placeholder="Enter approved amount" />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes / Reason</label>
                  <textarea className="form-textarea" style={{ minHeight:70, fontSize:12 }}
                    placeholder="Add notes for the initiator…"
                    value={approverNotes} onChange={e => setApproverNotes(e.target.value)} />
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:8, marginTop:8 }}>
                  {request.status !== 'under_review' && (
                    <button className="btn btn-ghost btn-sm" style={{ borderColor:'var(--sky)', color:'var(--sky)' }}
                      onClick={() => handleAction('review')} disabled={saving}>
                      🔍 Mark Under Review
                    </button>
                  )}
                  <button className="btn btn-primary btn-sm" style={{ background:'#0FA86A', border:'none' }}
                    onClick={() => handleAction('approve')} disabled={saving}>
                    ✅ Approve Request
                  </button>
                  <button className="btn btn-primary btn-sm" style={{ background:'#D63A3A', border:'none' }}
                    onClick={() => handleAction('decline')} disabled={saving}>
                    ❌ Decline Request
                  </button>
                </div>
              </div>
            )}

            {/* Resubmit for declined */}
            {isInitiator && (request.status === 'declined' || request.status === 'bm_declined') && (
              <div style={{ background:'#FDEAEA', border:'1px solid #D63A3A', borderRadius:'var(--radius)', padding:14, marginBottom:14 }}>
                <div style={{ fontSize:12, fontWeight:600, color:'#D63A3A', marginBottom:8 }}>
                  ❌ {request.status === 'bm_declined' ? 'Declined by Branch Manager' : 'Request Declined'}
                </div>
                <div style={{ fontSize:11, color:'var(--text2)', marginBottom:12, lineHeight:1.6 }}>
                  Your request was declined. You may resubmit with updated information.
                </div>
                <button className="btn btn-primary btn-sm" style={{ width:'100%' }} onClick={handleResubmit}>
                  🔄 Resubmit Request
                </button>
              </div>
            )}

            {/* Approval confirmation + PDF */}
            {request.status === 'approved' && (
              <div style={{ background:'#E6F7F1', border:'1px solid #0FA86A', borderRadius:'var(--radius)', padding:14, marginBottom:14, textAlign:'center' }}>
                <div style={{ fontSize:24, marginBottom:6 }}>✅</div>
                <div style={{ fontSize:12, fontWeight:600, color:'#0FA86A', marginBottom:4 }}>Request Approved</div>
                <div style={{ fontSize:18, fontWeight:700, color:'#0FA86A' }}>{fmt(request.amount_approved)}</div>
                {request.decided_at && <div style={{ fontSize:10, color:'var(--text3)', marginTop:6 }}>{format(new Date(request.decided_at),'dd MMM yyyy, HH:mm')}</div>}
                <button className="btn btn-primary btn-sm" style={{ marginTop:12, width:'100%', background:'#0FA86A', border:'none' }} onClick={downloadPDF}>
                  📄 Download Receipt (PDF)
                </button>
              </div>
            )}

            {/* Info fields */}
            {[
              { label:'Assigned Approver', val: request.approver_name || 'Unassigned' },
              { label:'Branch', val: request.branch },
              { label:'Submitted By', val: request.initiator_name },
              ...(request.bm_name ? [{ label:'Branch Manager', val: request.bm_name }] : []),
              ...(request.bm_recommended_at ? [{ label:'BM Recommended', val: format(new Date(request.bm_recommended_at),'dd MMM yyyy, HH:mm') }] : []),
              ...(request.decided_at ? [{ label:'Final Decision', val: format(new Date(request.decided_at),'dd MMM yyyy, HH:mm') }] : []),
            ].map(f => (
              <div key={f.label} className="side-field">
                <div className="side-label">{f.label}</div>
                <div className="side-val">{f.val}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
